/**
 * Validated Escalation Form
 * 
 * Production-ready form for escalation path management with SLA tracking
 */

import {
    Alert,
    Badge,
    Button,
    Card,
    Checkbox,
    Group,
    JsonInput,
    MultiSelect,
    NumberInput,
    Paper,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title,
    Tabs,
    Grid,
    Timeline,
    Table,
    ActionIcon,
    Progress
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
    IconCheck,
    IconInfoCircle,
    IconX,
    IconAlertTriangle,
    IconUsers,
    IconClock,
    IconArrowUp,
    IconPlus,
    IconTrash
} from '@tabler/icons-react';
import React, { useState } from 'react';
import { useValidatedForm, useValidatedFormSubmission } from '../hooks/useValidatedForm';
import { useEscalationMutations } from '../hooks/useWorkflowState';

interface EscalationLevel {
    level: number;
    name: string;
    assignees: string[];
    timeoutMinutes: number;
    actions: Array<{
        type: 'email' | 'sms' | 'webhook' | 'create_ticket';
        config: Record<string, any>;
    }>;
    conditions?: string[];
}

interface SLAConfig {
    responseTime: number;
    resolutionTime: number;
    escalationTime: number;
    businessHours: {
        enabled: boolean;
        startTime: string;
        endTime: string;
        workDays: string[];
        timezone: string;
    };
    penalties: Array<{
        threshold: number;
        type: 'warning' | 'penalty' | 'breach';
        action: string;
    }>;
}

interface EscalationData {
    name: string;
    description?: string;
    trigger: 'timeout' | 'manual' | 'condition' | 'sla_breach';
    category: 'technical' | 'business' | 'security' | 'compliance';
    priority: 'low' | 'medium' | 'high' | 'critical';
    levels: EscalationLevel[];
    slaConfig: SLAConfig;
    autoResolve: boolean;
    maxEscalations?: number;
    cooldownMinutes?: number;
    notifications: {
        onEscalation: boolean;
        onResolution: boolean;
        onSLABreach: boolean;
        templates: {
            escalation?: string;
            resolution?: string;
            slaBreach?: string;
        };
    };
    enabled: boolean;
    metadata?: Record<string, any>;
}

interface ValidatedEscalationFormProps {
    initialData?: Partial<EscalationData>;
    mode: 'create' | 'edit';
    onSuccess?: (data: EscalationData) => void;
    onCancel?: () => void;
}

export const ValidatedEscalationForm: React.FC<ValidatedEscalationFormProps> = ({
    initialData,
    mode,
    onSuccess,
    onCancel
}) => {
    const { createEscalation, updateEscalation } = useEscalationMutations();
    const [activeTab, setActiveTab] = useState<string | null>('basic');

    // Initialize validated form
    const form = useValidatedForm<Partial<EscalationData>>({
        schemaName: 'Escalation' as any,
        initialValues: {
            name: initialData?.name || '',
            description: initialData?.description || '',
            trigger: initialData?.trigger || 'timeout',
            category: initialData?.category || 'technical',
            priority: initialData?.priority || 'medium',
            levels: initialData?.levels || [
                {
                    level: 1,
                    name: 'First Level Support',
                    assignees: [],
                    timeoutMinutes: 30,
                    actions: [],
                    conditions: []
                }
            ],
            slaConfig: initialData?.slaConfig || {
                responseTime: 60,
                resolutionTime: 240,
                escalationTime: 30,
                businessHours: {
                    enabled: true,
                    startTime: '09:00',
                    endTime: '17:00',
                    workDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
                },
                penalties: []
            },
            autoResolve: initialData?.autoResolve || false,
            maxEscalations: initialData?.maxEscalations || undefined,
            cooldownMinutes: initialData?.cooldownMinutes || 60,
            notifications: initialData?.notifications || {
                onEscalation: true,
                onResolution: true,
                onSLABreach: true,
                templates: {}
            },
            enabled: initialData?.enabled !== undefined ? initialData.enabled : true,
            metadata: initialData?.metadata || {}
        },
        validateOnChange: true,
        validateOnBlur: true,
        showNotifications: false
    });

    // Form submission with GraphQL
    const { submitForm, loading: submitting, submissionResult } = useValidatedFormSubmission(
        'Escalation' as any,
        () => ({
            mutate: async (data: Partial<EscalationData>) => {
                if (mode === 'create') {
                    return await createEscalation(data as any);
                } else {
                    return await updateEscalation(initialData?.name || '', data as any);
                }
            },
            loading: false,
            error: null
        })
    );

    // Trigger options
    const triggerOptions = [
        { value: 'timeout', label: 'Timeout - Based on time elapsed' },
        { value: 'manual', label: 'Manual - Manually triggered escalation' },
        { value: 'condition', label: 'Condition - Based on specific conditions' },
        { value: 'sla_breach', label: 'SLA Breach - When SLA is violated' }
    ];

    // Category options
    const categoryOptions = [
        { value: 'technical', label: 'Technical - System and infrastructure issues' },
        { value: 'business', label: 'Business - Business process escalations' },
        { value: 'security', label: 'Security - Security incidents and breaches' },
        { value: 'compliance', label: 'Compliance - Regulatory compliance issues' }
    ];

    // Priority options
    const priorityOptions = [
        { value: 'low', label: 'Low Priority' },
        { value: 'medium', label: 'Medium Priority' },
        { value: 'high', label: 'High Priority' },
        { value: 'critical', label: 'Critical Priority' }
    ];

    // Action type options
    const actionTypeOptions = [
        { value: 'email', label: 'Email Notification' },
        { value: 'sms', label: 'SMS Alert' },
        { value: 'webhook', label: 'Webhook Call' },
        { value: 'create_ticket', label: 'Create Support Ticket' }
    ];

    // Work days
    const workDaysOptions = [
        'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
    ];

    // Add escalation level
    const addEscalationLevel = () => {
        const currentLevels = form.values.levels || [];
        const newLevel: EscalationLevel = {
            level: currentLevels.length + 1,
            name: `Level ${currentLevels.length + 1}`,
            assignees: [],
            timeoutMinutes: 60,
            actions: [],
            conditions: []
        };
        form.setFieldValue('levels', [...currentLevels, newLevel]);
    };

    // Remove escalation level
    const removeEscalationLevel = (index: number) => {
        const currentLevels = form.values.levels || [];
        const updatedLevels = currentLevels.filter((_: any, i: number) => i !== index)
            .map((level: any, i: number) => ({ ...level, level: i + 1, name: level.name.includes('Level') ? `Level ${i + 1}` : level.name }));
        form.setFieldValue('levels', updatedLevels);
    };

    // Update escalation level
    const updateEscalationLevel = (index: number, field: string, value: any) => {
        const currentLevels = [...(form.values.levels || [])];
        currentLevels[index] = { ...currentLevels[index], [field]: value };
        form.setFieldValue('levels', currentLevels);
    };

    // Add action to level
    const addActionToLevel = (levelIndex: number) => {
        const currentLevels = [...(form.values.levels || [])];
        const newAction = {
            type: 'email' as const,
            config: {}
        };
        currentLevels[levelIndex].actions = [...(currentLevels[levelIndex].actions || []), newAction];
        form.setFieldValue('levels', currentLevels);
    };

    // Remove action from level
    const removeActionFromLevel = (levelIndex: number, actionIndex: number) => {
        const currentLevels = [...(form.values.levels || [])];
        currentLevels[levelIndex].actions = currentLevels[levelIndex].actions.filter((_: any, i: number) => i !== actionIndex);
        form.setFieldValue('levels', currentLevels);
    };

    // Add SLA penalty
    const addSLAPenalty = () => {
        const currentPenalties = form.values.slaConfig?.penalties || [];
        const newPenalty = {
            threshold: 100,
            type: 'warning' as const,
            action: 'Log warning'
        };
        form.setFieldValue('slaConfig.penalties', [...currentPenalties, newPenalty]);
    };

    // Remove SLA penalty
    const removeSLAPenalty = (index: number) => {
        const currentPenalties = form.values.slaConfig?.penalties || [];
        const updatedPenalties = currentPenalties.filter((_: any, i: number) => i !== index);
        form.setFieldValue('slaConfig.penalties', updatedPenalties);
    };

    // Handle form submission
    const handleSubmit = async (values: Partial<EscalationData>) => {
        const result = await submitForm(values);
        if (result.success && onSuccess) {
            onSuccess(result.data);
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'critical': return 'red';
            case 'high': return 'orange';
            case 'medium': return 'yellow';
            case 'low': return 'blue';
            default: return 'gray';
        }
    };

    return (
        <form onSubmit={form.onSubmit((values) => handleSubmit(values as Partial<EscalationData>))}>
            <Stack gap="lg">
                {/* Header */}
                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Group>
                                <Title order={3}>
                                    {mode === 'create' ? 'Create' : 'Edit'} Escalation Path
                                </Title>
                                <IconAlertTriangle size={24} color="orange" />
                            </Group>
                            <Text size="sm" c="dimmed">
                                Configure escalation paths with SLA tracking and automated notifications
                            </Text>
                        </div>
                        <Group>
                            <Badge 
                                color={form.validationResult.valid ? 'green' : 'red'}
                                leftSection={form.validationResult.valid ? <IconCheck size={14} /> : <IconX size={14} />}
                            >
                                {form.validationResult.valid ? 'Valid' : `${form.validationResult.errors.length} Error${form.validationResult.errors.length > 1 ? 's' : ''}`}
                            </Badge>
                        </Group>
                    </Group>
                </Paper>

                {/* Validation Summary */}
                {form.validationResult.errors.length > 0 && (
                    <Alert color="red" title="Validation Errors" icon={<IconAlertTriangle />}>
                        <Stack gap="xs">
                            {form.validationResult.errors.map((error, index) => (
                                <Text key={index} size="sm">
                                    <strong>{error.field}:</strong> {error.message}
                                </Text>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {form.validationResult.warnings && form.validationResult.warnings.length > 0 && (
                    <Alert color="yellow" title="Validation Warnings" icon={<IconInfoCircle />}>
                        <Stack gap="xs">
                            {form.validationResult.warnings.map((warning, index) => (
                                <Text key={index} size="sm">
                                    <strong>{warning.field}:</strong> {warning.message}
                                    {warning.suggestion && <Text c="dimmed"> • {warning.suggestion}</Text>}
                                </Text>
                            ))}
                        </Stack>
                    </Alert>
                )}

                {/* Escalation Form Tabs */}
                <Tabs value={activeTab} onChange={(v) => setActiveTab(v ?? '')}>
                    <Tabs.List>
                        <Tabs.Tab value="basic" leftSection={<IconInfoCircle size={16} />}>
                            Basic Info
                        </Tabs.Tab>
                        <Tabs.Tab value="levels" leftSection={<IconArrowUp size={16} />}>
                            Levels ({form.values.levels?.length || 0})
                        </Tabs.Tab>
                        <Tabs.Tab value="sla" leftSection={<IconClock size={16} />}>
                            SLA Config
                        </Tabs.Tab>
                        <Tabs.Tab value="notifications" leftSection={<IconUsers size={16} />}>
                            Notifications
                        </Tabs.Tab>
                    </Tabs.List>

                    {/* Basic Information Tab */}
                    <Tabs.Panel value="basic">
                        <Card withBorder mt="md">
                            <Stack gap="md">
                                <TextInput
                                    label="Escalation Name"
                                    placeholder="Critical Issue Escalation"
                                    required
                                    {...form.getInputProps('name')}
                                    error={form.getFieldError('name')}
                                    rightSection={
                                        form.isFieldValid('name') && form.values.name ? 
                                        <IconCheck size={16} color="green" /> : undefined
                                    }
                                />

                                <Textarea
                                    label="Description"
                                    placeholder="Escalation path for critical system issues requiring immediate attention..."
                                    minRows={3}
                                    {...form.getInputProps('description')}
                                    error={form.getFieldError('description')}
                                />

                                <Group grow>
                                    <Select
                                        label="Trigger Type"
                                        data={triggerOptions}
                                        required
                                        {...form.getInputProps('trigger')}
                                        error={form.getFieldError('trigger')}
                                    />

                                    <Select
                                        label="Category"
                                        data={categoryOptions}
                                        required
                                        {...form.getInputProps('category')}
                                        error={form.getFieldError('category')}
                                    />
                                </Group>

                                <Group grow>
                                    <Select
                                        label="Priority"
                                        data={priorityOptions}
                                        {...form.getInputProps('priority')}
                                        error={form.getFieldError('priority')}
                                        rightSection={
                                            <Badge size="xs" color={getPriorityColor(form.values.priority || '')}>
                                                {form.values.priority}
                                            </Badge>
                                        }
                                    />

                                    <Checkbox
                                        label="Escalation Enabled"
                                        description="Enable this escalation path"
                                        {...form.getInputProps('enabled', { type: 'checkbox' })}
                                    />
                                </Group>

                                <Group grow>
                                    <NumberInput
                                        label="Max Escalations"
                                        placeholder="Unlimited"
                                        description="Maximum number of escalations before stopping"
                                        min={1}
                                        {...form.getInputProps('maxEscalations')}
                                        error={form.getFieldError('maxEscalations')}
                                    />

                                    <NumberInput
                                        label="Cooldown Period (minutes)"
                                        description="Wait time before allowing re-escalation"
                                        min={0}
                                        {...form.getInputProps('cooldownMinutes')}
                                        error={form.getFieldError('cooldownMinutes')}
                                    />
                                </Group>

                                <Checkbox
                                    label="Auto-Resolve"
                                    description="Automatically resolve escalation when conditions are met"
                                    {...form.getInputProps('autoResolve', { type: 'checkbox' })}
                                />
                            </Stack>
                        </Card>
                    </Tabs.Panel>

                    {/* Escalation Levels Tab */}
                    <Tabs.Panel value="levels">
                        <Card withBorder mt="md">
                            <Group justify="space-between" mb="md">
                                <Title order={4}>Escalation Levels</Title>
                                <Button
                                    size="sm"
                                    variant="light"
                                    leftSection={<IconPlus size={16} />}
                                    onClick={addEscalationLevel}
                                >
                                    Add Level
                                </Button>
                            </Group>

                            <Stack gap="lg">
                                {form.values.levels && form.values.levels.length > 0 ? (
                                    form.values.levels.map((level: any, index: number) => (
                                        <Card key={index} withBorder p="md">
                                            <Group justify="space-between" mb="md">
                                                <Group>
                                                    <Badge color="blue">Level {level.level}</Badge>
                                                    <Text fw={500}>{level.name}</Text>
                                                </Group>
                                                <ActionIcon
                                                    color="red"
                                                    variant="subtle"
                                                    onClick={() => removeEscalationLevel(index)}
                                                >
                                                    <IconTrash size={16} />
                                                </ActionIcon>
                                            </Group>

                                            <Grid>
                                                <Grid.Col span={6}>
                                                    <TextInput
                                                        label="Level Name"
                                                        value={level.name}
                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateEscalationLevel(index, 'name', e.currentTarget.value)}
                                                    />
                                                </Grid.Col>
                                                <Grid.Col span={6}>
                                                    <NumberInput
                                                        label="Timeout (minutes)"
                                                        min={1}
                                                        value={level.timeoutMinutes}
                                                        onChange={(value: string | number | undefined) => updateEscalationLevel(index, 'timeoutMinutes', value)}
                                                    />
                                                </Grid.Col>
                                            </Grid>

                                            <MultiSelect
                                                label="Assignees"
                                                placeholder="Enter email addresses or user IDs"
                                                data={[]}
                                                searchable
                                                value={level.assignees}
                                                onChange={(values: string[]) => updateEscalationLevel(index, 'assignees', values)}
                                                mt="md"
                                            />

                                            <Group justify="space-between" mt="md">
                                                <Text size="sm" fw={500}>Actions ({level.actions?.length || 0})</Text>
                                                <Button
                                                    size="xs"
                                                    variant="light"
                                                    onClick={() => addActionToLevel(index)}
                                                >
                                                    Add Action
                                                </Button>
                                            </Group>

                                            {level.actions && level.actions.length > 0 && (
                                                <Stack gap="xs" mt="sm">
                                                    {level.actions.map((action: any, actionIndex: number) => (
                                                        <Group key={actionIndex} ta="center">
                                                            <Select
                                                                data={actionTypeOptions}
                                                                value={action.type}
                                                                onChange={(value: string | null) => {
                                                                    const currentLevels = [...(form.values.levels || [])];
                                                                    currentLevels[index].actions[actionIndex].type = value as any;
                                                                    form.setFieldValue('levels', currentLevels);
                                                                }}
                                                                style={{ flex: 1 }}
                                                            />
                                                            <ActionIcon
                                                                color="red"
                                                                variant="subtle"
                                                                size="sm"
                                                                onClick={() => removeActionFromLevel(index, actionIndex)}
                                                            >
                                                                <IconTrash size={14} />
                                                            </ActionIcon>
                                                        </Group>
                                                    ))}
                                                </Stack>
                                            )}
                                        </Card>
                                    ))
                                ) : (
                                    <Text c="dimmed" ta="center" py="xl">
                                        No escalation levels defined. Add levels to create escalation path.
                                    </Text>
                                )}
                            </Stack>
                        </Card>
                    </Tabs.Panel>

                    {/* SLA Configuration Tab */}
                    <Tabs.Panel value="sla">
                        <Card withBorder mt="md">
                            <Stack gap="md">
                                <Title order={4}>SLA Configuration</Title>
                                
                                <Grid>
                                    <Grid.Col span={4}>
                                        <NumberInput
                                            label="Response Time (minutes)"
                                            description="Time to acknowledge the issue"
                                            min={1}
                                            value={form.values.slaConfig?.responseTime}
                                            onChange={(value: string | number | undefined) => form.setFieldValue('slaConfig.responseTime', value)}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={4}>
                                        <NumberInput
                                            label="Resolution Time (minutes)"
                                            description="Time to resolve the issue"
                                            min={1}
                                            value={form.values.slaConfig?.resolutionTime}
                                            onChange={(value: string | number | undefined) => form.setFieldValue('slaConfig.resolutionTime', value)}
                                        />
                                    </Grid.Col>
                                    <Grid.Col span={4}>
                                        <NumberInput
                                            label="Escalation Time (minutes)"
                                            description="Time before escalation"
                                            min={1}
                                            value={form.values.slaConfig?.escalationTime}
                                            onChange={(value: string | number | undefined) => form.setFieldValue('slaConfig.escalationTime', value)}
                                        />
                                    </Grid.Col>
                                </Grid>

                                <Title order={5} mt="md">Business Hours</Title>
                                <Checkbox
                                    label="Enable Business Hours"
                                    description="SLA calculations only apply during business hours"
                                    checked={form.values.slaConfig?.businessHours?.enabled || false}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('slaConfig.businessHours.enabled', e.currentTarget.checked)}
                                />

                                {form.values.slaConfig?.businessHours?.enabled && (
                                    <>
                                        <Group grow>
                                            <TextInput
                                                label="Start Time"
                                                type="time"
                                                value={form.values.slaConfig.businessHours.startTime || '09:00'}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('slaConfig.businessHours.startTime', e.currentTarget.value)}
                                            />
                                            <TextInput
                                                label="End Time"
                                                type="time"
                                                value={form.values.slaConfig.businessHours.endTime || '17:00'}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('slaConfig.businessHours.endTime', e.currentTarget.value)}
                                            />
                                        </Group>

                                        <MultiSelect
                                            label="Work Days"
                                            data={workDaysOptions.map(day => ({ value: day, label: day }))}
                                            value={form.values.slaConfig.businessHours.workDays || []}
                                            onChange={(values: string[]) => form.setFieldValue('slaConfig.businessHours.workDays', values)}
                                        />
                                    </>
                                )}

                                <Group justify="space-between" mt="md">
                                    <Title order={5}>SLA Penalties</Title>
                                    <Button size="sm" variant="light" onClick={addSLAPenalty}>
                                        Add Penalty
                                    </Button>
                                </Group>

                                {form.values.slaConfig?.penalties && form.values.slaConfig.penalties.length > 0 ? (
                                    <Table>
                                        <Table.Thead>
                                            <Table.Tr>
                                                <Table.Th>Threshold (%)</Table.Th>
                                                <Table.Th>Type</Table.Th>
                                                <Table.Th>Action</Table.Th>
                                                <Table.Th>Actions</Table.Th>
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {form.values.slaConfig.penalties.map((penalty: any, index: number) => (
                                                <Table.Tr key={index}>
                                                    <Table.Td>
                                                        <NumberInput
                                                            size="sm"
                                                            min={0}
                                                            max={200}
                                                            value={penalty.threshold}
                                                            onChange={(value: string | number | undefined) => {
                                                                const currentPenalties = [...(form.values.slaConfig?.penalties || [])];
                                                                currentPenalties[index].threshold = value || 0;
                                                                form.setFieldValue('slaConfig.penalties', currentPenalties);
                                                            }}
                                                        />
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Select
                                                            size="sm"
                                                            data={[
                                                                { value: 'warning', label: 'Warning' },
                                                                { value: 'penalty', label: 'Penalty' },
                                                                { value: 'breach', label: 'Breach' }
                                                            ]}
                                                            value={penalty.type}
                                                            onChange={(value: string | null) => {
                                                                const currentPenalties = [...(form.values.slaConfig?.penalties || [])];
                                                                currentPenalties[index].type = value as any;
                                                                form.setFieldValue('slaConfig.penalties', currentPenalties);
                                                            }}
                                                        />
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <TextInput
                                                            size="sm"
                                                            value={penalty.action}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                const currentPenalties = [...(form.values.slaConfig?.penalties || [])];
                                                                currentPenalties[index].action = e.currentTarget.value;
                                                                form.setFieldValue('slaConfig.penalties', currentPenalties);
                                                            }}
                                                        />
                                                    </Table.Td>
                                                    <Table.Td>
                                                        <Button
                                                            size="xs"
                                                            variant="light"
                                                            color="red"
                                                            onClick={() => removeSLAPenalty(index)}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </Table.Td>
                                                </Table.Tr>
                                            ))}
                                        </Table.Tbody>
                                    </Table>
                                ) : (
                                    <Text c="dimmed" ta="center" py="md">
                                        No SLA penalties configured
                                    </Text>
                                )}
                            </Stack>
                        </Card>
                    </Tabs.Panel>

                    {/* Notifications Tab */}
                    <Tabs.Panel value="notifications">
                        <Card withBorder mt="md">
                            <Stack gap="md">
                                <Title order={4}>Notification Settings</Title>
                                
                                <Group>
                                    <Checkbox
                                        label="Notify on Escalation"
                                        description="Send notifications when escalation occurs"
                                        checked={form.values.notifications?.onEscalation || false}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('notifications.onEscalation', e.currentTarget.checked)}
                                    />
                                    <Checkbox
                                        label="Notify on Resolution"
                                        description="Send notifications when escalation is resolved"
                                        checked={form.values.notifications?.onResolution || false}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('notifications.onResolution', e.currentTarget.checked)}
                                    />
                                    <Checkbox
                                        label="Notify on SLA Breach"
                                        description="Send notifications when SLA is breached"
                                        checked={form.values.notifications?.onSLABreach || false}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.setFieldValue('notifications.onSLABreach', e.currentTarget.checked)}
                                    />
                                </Group>

                                <Title order={5} mt="md">Notification Templates</Title>
                                <Stack gap="md">
                                    <Textarea
                                        label="Escalation Template"
                                        placeholder="Issue #{id} has been escalated to {level}..."
                                        minRows={3}
                                        value={form.values.notifications?.templates?.escalation || ''}
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => form.setFieldValue('notifications.templates.escalation', e.currentTarget.value)}
                                    />

                                    <Textarea
                                        label="Resolution Template"
                                        placeholder="Issue #{id} has been resolved..."
                                        minRows={3}
                                        value={form.values.notifications?.templates?.resolution || ''}
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => form.setFieldValue('notifications.templates.resolution', e.currentTarget.value)}
                                    />

                                    <Textarea
                                        label="SLA Breach Template"
                                        placeholder="SLA breach detected for issue #{id}..."
                                        minRows={3}
                                        value={form.values.notifications?.templates?.slaBreach || ''}
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => form.setFieldValue('notifications.templates.slaBreach', e.currentTarget.value)}
                                    />
                                </Stack>

                                <JsonInput
                                    label="Additional Metadata (JSON)"
                                    placeholder='{ "department": "support", "severity": "high" }'
                                    validationError="Invalid JSON"
                                    formatOnBlur
                                    autosize
                                    minRows={4}
                                    maxRows={8}
                                    value={typeof form.values.metadata === 'string' ? 
                                        form.values.metadata : 
                                        JSON.stringify(form.values.metadata || {}, null, 2)
                                    }
                                    onChange={(value) => {
                                        try {
                                            const parsed = JSON.parse(value || '{}');
                                            form.setFieldValue('metadata', parsed);
                                        } catch {
                                            form.setFieldValue('metadata', value);
                                        }
                                    }}
                                    error={form.getFieldError('metadata')}
                                />
                            </Stack>
                        </Card>
                    </Tabs.Panel>
                </Tabs>

                {/* Form Actions */}
                <Paper p="md" withBorder>
                    <Group justify="space-between">
                        <div>
                            <Text size="sm" c="dimmed">
                                Escalation will be validated before submission
                            </Text>
                            {form.isValidating && (
                                <Text size="sm" c="blue">
                                    Validating...
                                </Text>
                            )}
                        </div>
                        
                        <Group>
                            <Button variant="light" onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                loading={submitting || form.isValidating}
                                disabled={!form.validationResult.valid}
                            >
                                {mode === 'create' ? 'Create Escalation' : 'Update Escalation'}
                            </Button>
                        </Group>
                    </Group>
                </Paper>

                {/* Submission Result */}
                {submissionResult && (
                    <Alert 
                        color={submissionResult.success ? 'green' : 'red'}
                        title={submissionResult.success ? 'Success' : 'Error'}
                    >
                        {submissionResult.success ? 
                            'Escalation saved successfully!' : 
                            submissionResult.error
                        }
                    </Alert>
                )}
            </Stack>
        </form>
    );
};

export default ValidatedEscalationForm;